import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh, Save, CloudDownload } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  fetchEffectiveAppConfig,
  fetchGlobalAppConfig,
  saveTenantAppConfig,
  saveGlobalAppConfig,
  syncAppConfigFromGlobal,
} from '../services/appConfigApi';
import { useAppConfig } from '../context/AppConfigContext';

const DATE_OPTIONS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];

const CATEGORY_TITLE = {
  UI: 'Display & formats',
  STORAGE: 'Storage',
  FEATURE: 'Feature toggles',
};

const AppConfig = () => {
  const isCdx = localStorage.getItem('cdx_user') === 'true';
  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
  const { refresh: refreshRuntime } = useAppConfig();
  const [mode, setMode] = useState('tenant');
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data =
        mode === 'global' && isCdx
          ? await fetchGlobalAppConfig()
          : await fetchEffectiveAppConfig(tenantId);
      const normalized = (data || []).map((r) => ({
        configKey: r.configKey || r.config_key,
        category: r.category || 'OTHER',
        valueType: r.valueType || r.value_type,
        value1: r.value1 ?? '',
        value2: r.value2 ?? '',
        value3: r.value3 ?? '',
        source: r.source,
        globalValue1: r.globalValue1,
        overridden: !!r.overridden,
      }));
      setRows(normalized);
      const d = {};
      normalized.forEach((r) => {
        d[r.configKey] = r.value1 ?? '';
      });
      setDrafts(d);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [mode, isCdx, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const order = ['UI', 'STORAGE', 'FEATURE'];
    const map = {};
    rows.forEach((r) => {
      const cat = r.category || 'OTHER';
      if (!map[cat]) map[cat] = [];
      map[cat].push(r);
    });
    const cats = [
      ...order.filter((c) => map[c]?.length),
      ...Object.keys(map).filter((c) => !order.includes(c)),
    ];
    return cats.map((cat) => ({
      cat,
      title: CATEGORY_TITLE[cat] || cat,
      rows: map[cat],
    }));
  }, [rows]);

  const isDirty = (row) => (drafts[row.configKey] ?? '') !== (row.value1 ?? '');

  const saveRow = async (row) => {
    setSaving(row.configKey);
    try {
      const body = {
        value1: drafts[row.configKey],
        value2: row.value2,
        value3: row.value3,
        category: row.category,
        valueType: row.valueType,
      };
      if (mode === 'global' && isCdx) {
        await saveGlobalAppConfig(row.configKey, body);
      } else {
        await saveTenantAppConfig(row.configKey, body, tenantId);
      }
      toast.success(`Saved ${row.value2 || row.configKey}`);
      await load();
      if (mode === 'tenant' || (mode === 'global' && isCdx)) {
        await refreshRuntime();
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  const syncMissing = async () => {
    setSyncing(true);
    try {
      await syncAppConfigFromGlobal(tenantId);
      toast.success('Synced missing keys from global');
      await load();
      await refreshRuntime();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const renderEditor = (row) => {
    const key = row.configKey;
    const val = drafts[key] ?? '';

    if (key === 'DATE_FORMAT') {
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={DATE_OPTIONS.includes(val) ? val : val || 'MM/DD/YYYY'}
            onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
          >
            {DATE_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (row.valueType === 'BOOLEAN') {
      const on = String(val) !== 'false';
      return (
        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              size="small"
              checked={on}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [key]: e.target.checked ? 'true' : 'false' }))
              }
            />
          }
          label={on ? 'Enabled' : 'Disabled'}
        />
      );
    }

    return (
      <TextField
        size="small"
        value={val}
        onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
        placeholder={row.value3 || ''}
        sx={{ minWidth: 180, maxWidth: 280 }}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1, minWidth: 200 }}>
          Application Configuration
        </Typography>
        {isCdx && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Scope</InputLabel>
            <Select label="Scope" value={mode} onChange={(e) => setMode(e.target.value)}>
              <MenuItem value="tenant">This tenant</MenuItem>
              <MenuItem value="global">Global master</MenuItem>
            </Select>
          </FormControl>
        )}
        {mode === 'tenant' && (
          <Button
            size="small"
            startIcon={<CloudDownload />}
            variant="outlined"
            onClick={syncMissing}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync from global'}
          </Button>
        )}
        <Button size="small" startIcon={<Refresh />} variant="outlined" onClick={load}>
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {mode === 'global'
          ? 'Global defaults for new tenants and fallback when a tenant has no override.'
          : 'Tenant values override global defaults. Save only updates this tenant.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : grouped.length === 0 ? (
        <Alert severity="info">
          No configuration rows found. Use Sync from global or seed master defaults.
        </Alert>
      ) : (
        grouped.map((section) => (
          <Box key={section.cat} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {section.title}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '28%' }}>Setting</TableCell>
                  <TableCell sx={{ width: '32%' }}>Value</TableCell>
                  <TableCell sx={{ width: '16%' }}>Source</TableCell>
                  {mode === 'tenant' && <TableCell sx={{ width: '16%' }}>Global default</TableCell>}
                  <TableCell width={90} />
                </TableRow>
              </TableHead>
              <TableBody>
                {section.rows.map((row) => {
                  const dirty = isDirty(row);
                  return (
                    <TableRow key={row.configKey} hover selected={dirty}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.value2 || row.configKey}
                        </Typography>
                        {row.value3 ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row.value3}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{renderEditor(row)}</TableCell>
                      <TableCell>
                        {mode === 'tenant' ? (
                          <Chip
                            size="small"
                            label={row.overridden || dirty ? 'Override' : 'Default'}
                            color={row.overridden || dirty ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        ) : (
                          <Chip size="small" label="Global" variant="outlined" />
                        )}
                      </TableCell>
                      {mode === 'tenant' && (
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.globalValue1 != null && row.globalValue1 !== ''
                              ? String(row.globalValue1)
                              : '—'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Button
                          size="small"
                          variant={dirty ? 'contained' : 'outlined'}
                          startIcon={<Save />}
                          disabled={saving === row.configKey || !dirty}
                          onClick={() => saveRow(row)}
                        >
                          {saving === row.configKey ? '…' : 'Save'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ))
      )}
    </Box>
  );
};

export default AppConfig;
